import { useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react'
import { Indicator } from './Spin'
import { cn } from './internal/utils'
import { BORDER, RADIUS } from './internal/presets'
import { destroyAll, push, remove, subscribe } from './internal/messageStore'
import { mountDetached } from './internal/reactRootHost'

const ICONS = {
  success: { Icon: CheckCircle2, color: 'var(--success-green)' },
  error: { Icon: XCircle, color: 'var(--error-color)' },
  warning: { Icon: AlertTriangle, color: 'var(--warning-color)' },
  info: { Icon: Info, color: 'var(--info-blue)' },
}

const MessageHost = () => {
  const [entries, setEntries] = useState([])

  useEffect(() => subscribe(setEntries), [])

  if (entries.length === 0) return null

  return (
    <div
      className="ui-message pointer-events-none fixed left-1/2 top-4 flex -translate-x-1/2 flex-col items-center gap-2"
      style={{ zIndex: 'var(--z-toast, 2100)' }}
      // Announces new toasts without stealing focus.
      role="status"
      aria-live="polite"
    >
      {entries.map((entry) => {
        const preset = ICONS[entry.type]
        return (
          <div
            key={entry.id}
            className={cn(
              'ui-message-notice pointer-events-auto flex max-w-[90vw] items-center gap-2 border bg-[var(--surface-card)] px-3 py-2 text-sm shadow-[var(--shadow-md)]',
              BORDER,
              RADIUS,
              entry.className
            )}
            style={entry.style}
          >
            <span className="shrink-0" style={{ color: preset?.color }}>
              {entry.icon ??
                (entry.type === 'loading' ? (
                  <Indicator size="small" />
                ) : preset ? (
                  <preset.Icon size={16} aria-hidden="true" />
                ) : null)}
            </span>
            <span className="ui-message-custom-content min-w-0">{entry.content}</span>
            {entry.duration === 0 ? (
              <button
                type="button"
                aria-label="Fermer"
                onClick={() => remove(entry.id)}
                className="ml-1 shrink-0 rounded p-1 text-[var(--gray-600)] hover:text-[var(--text-body)]"
              >
                &times;
              </button>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

let host = null

const ensureHost = () => {
  if (host || typeof document === 'undefined') return
  host = mountDetached(() => <MessageHost />)
}

/** antd accepts either a content node or a full config object. */
const toConfig = (type, content, duration, onClose) => {
  if (content != null && typeof content === 'object' && !Array.isArray(content) && 'content' in content) {
    return { type, ...content }
  }
  // antd allows message.success('x', onClose) with the callback in the duration slot.
  if (typeof duration === 'function') return { type, content, onClose: duration }
  return { type, content, duration, onClose }
}

const show = (type) => (content, duration, onClose) => {
  ensureHost()
  return push(toConfig(type, content, duration, onClose))
}

const message = {
  success: show('success'),
  error: show('error'),
  warning: show('warning'),
  warn: show('warning'),
  info: show('info'),
  loading: (content, duration, onClose) => {
    ensureHost()
    // antd's loading toast defaults to staying open.
    const config = toConfig('loading', content, duration, onClose)
    return push({ ...config, duration: config.duration ?? 0 })
  },
  open: (config) => {
    ensureHost()
    return push({ type: config?.type ?? 'info', ...config })
  },
  destroy: (key) => destroyAll(key),
  config: () => {},
  /** antd's hook API; the holder is unnecessary because the host is global. */
  useMessage: () => [message, null],
}

export { MessageHost }
export default message
